/**
 * Fichiers qu'un message n'a pas pu emporter, en attente du composeur de LEUR
 * conversation (#2066).
 *
 * Un message part désormais même si le téléversement d'une de ses pièces
 * échoue ; la pièce restée doit revenir sous les yeux de l'avocat. La rendre au
 * composeur qui a lancé l'envoi ne tient pas : ce composeur est démonté pendant
 * l'envoi dans les cas les plus courants —
 * - au premier message, l'écran d'accueil cède la place au fil, qui monte son
 *   propre composeur (et `/assistant` remonte tout le chat en écrivant `?c=`) ;
 * - l'avocat change de conversation pendant que 50 fichiers se téléversent.
 * Les fichiers passent donc par ce relais, indexé par conversation et vivant
 * hors de l'arbre React : le composeur monté pour cette conversation — celui-ci
 * ou le prochain — les reprend en les AJOUTANT à ce qu'il porte déjà.
 *
 * Rien n'est persisté (un `File` ne survit pas à un rechargement) ni envoyé
 * automatiquement.
 *
 * DURÉE DE VIE. Un fichier collé ou converti vit en mémoire, jusqu'à 50 Mo : le
 * relais ne le retient pas au-delà de ce qui peut encore le reprendre. Il sort
 * - quand un composeur de sa conversation l'a repris ;
 * - quand la session de sa conversation quitte le pool — trente minutes sans
 *   activité, place reprise par une autre conversation, conversation supprimée
 *   (`handleSessionRemoved`) ;
 * - au changement d'utilisateur.
 * « Vous le retrouverez dans la zone de saisie de sa conversation » vaut donc
 * tant que cette conversation reste ouverte dans l'onglet.
 */

import { useCallback, useEffect, useState } from 'react';
import { create } from 'zustand';

/** Liste vide partagée : une référence stable n'éveille aucun abonné. */
const AUCUN_FICHIER: readonly File[] = [];

interface FichiersRendusAuComposeurStore {
  readonly parConversation: Readonly<Record<string, readonly File[]>>;
  readonly rendre: (conversationId: string, files: readonly File[]) => void;
  readonly confirmer: (conversationId: string, files: readonly File[]) => void;
  readonly oublierConversation: (conversationId: string) => void;
  readonly oublier: () => void;
}

const useFichiersRendusAuComposeurStore = create<FichiersRendusAuComposeurStore>()((set) => ({
  parConversation: {},
  rendre: (conversationId, files) => set((state) => {
    const enAttente = state.parConversation[conversationId] ?? AUCUN_FICHIER;
    // Par IDENTITÉ et non par nom : deux pièces peuvent porter le même nom.
    const nouveaux = files.filter((file) => !enAttente.includes(file));
    if (nouveaux.length === 0) return state;
    return { parConversation: { ...state.parConversation, [conversationId]: [...enAttente, ...nouveaux] } };
  }),
  confirmer: (conversationId, files) => set((state) => {
    const enAttente = state.parConversation[conversationId];
    if (enAttente === undefined) return state;
    // Seuls les fichiers repris sortent : un envoi partiel peut en avoir rendu
    // d'autres entre le rendu du composeur et sa confirmation.
    const restants = enAttente.filter((file) => !files.includes(file));
    if (restants.length === enAttente.length) return state;
    const { [conversationId]: _repris, ...autres } = state.parConversation;
    return { parConversation: restants.length > 0 ? { ...autres, [conversationId]: restants } : autres };
  }),
  oublierConversation: (conversationId) => set((state) => {
    if (!(conversationId in state.parConversation)) return state;
    const { [conversationId]: _oublies, ...autres } = state.parConversation;
    return { parConversation: autres };
  }),
  oublier: () => set({ parConversation: {} }),
}));

/**
 * Remet au composeur de `conversationId` des fichiers que son message n'a pas
 * emportés. Sans effet sur une liste vide.
 */
export function rendreFichiersAuComposeur(conversationId: string, files: readonly File[]): void {
  useFichiersRendusAuComposeurStore.getState().rendre(conversationId, files);
}

/** Fichiers qui attendent le composeur de cette conversation. */
export function lireFichiersRendusAuComposeur(conversationId: string | null): readonly File[] {
  if (!conversationId) return AUCUN_FICHIER;
  return useFichiersRendusAuComposeurStore.getState().parConversation[conversationId] ?? AUCUN_FICHIER;
}

/** Le composeur a repris ces fichiers : ils quittent le relais. */
export function confirmerFichiersRendusAuComposeur(conversationId: string, files: readonly File[]): void {
  useFichiersRendusAuComposeurStore.getState().confirmer(conversationId, files);
}

/**
 * La conversation n'est plus ouverte dans l'onglet (session fermée, conversation
 * supprimée) : le relais cesse de retenir ses fichiers.
 */
export function oublierLesFichiersRendusDeLaConversation(conversationId: string): void {
  useFichiersRendusAuComposeurStore.getState().oublierConversation(conversationId);
}

/** Vide le relais — changement d'utilisateur, tests. */
export function oublierLesFichiersRendusAuComposeur(): void {
  useFichiersRendusAuComposeurStore.getState().oublier();
}

/** Ce qu'un hôte pose sur le composeur à la place de l'avocat ; seules ses pièces jointes comptent ici. */
interface BrouillonDeLHote {
  readonly files?: File[];
}

/** Les quatre props de pièces jointes de `HectorPromptInputBox`. */
interface PiecesJointesPoseesSurLeComposeur {
  /** Pièces du brouillon de l'hôte : REMPLACENT la sélection du composeur. */
  initialFiles: File[] | undefined;
  onInitialFilesConsumed: () => void;
  /** Fichiers rendus à cette conversation : S'AJOUTENT à la sélection. */
  returnedFiles: readonly File[];
  onReturnedFilesConsumed: (files: readonly File[]) => void;
}

/**
 * Les pièces jointes qu'un hôte pose sur le composeur de `conversationId` : celles
 * de son brouillon, et les fichiers que le relais garde pour cette conversation.
 *
 * ⚠️ L'ORDRE EST L'INVARIANT : le brouillon d'abord, les fichiers rendus ensuite.
 * Les pièces d'un brouillon REMPLACENT la sélection du composeur ; un fichier
 * rendu posé avant elles était repris par le composeur — donc retiré du relais —
 * puis écrasé un rendu plus tard, et n'existait plus nulle part. C'est ce qui
 * arrivait dans le dock d'un dossier, dont le composeur n'est pas remonté d'un
 * fil à l'autre : y rouvrir un fil vide le brouillon ET change la conversation
 * dans le même lot (`DossierCaseDiscoveryShell.openConversation`). Tant qu'un
 * brouillon de l'hôte est en cours de pose, rien n'est donc rendu ; le relais
 * garde les fichiers un rendu de plus et le composeur les reprend APRÈS.
 *
 * Tout nouveau composeur de `V3ChatInterface` reçoit ces props d'un bloc.
 */
export function usePiecesJointesPoseesSurLeComposeur(
  conversationId: string | null,
  brouillonDeLHote: BrouillonDeLHote | null,
): PiecesJointesPoseesSurLeComposeur {
  /** Pièces du brouillon en attente de pose ; `null` = rien à poser. */
  const [piecesDuBrouillon, setPiecesDuBrouillon] = useState<File[] | null>(null);
  /** Dernier brouillon dont les pièces ont été confiées au composeur. */
  const [brouillonConfie, setBrouillonConfie] = useState<BrouillonDeLHote | null>(null);
  useEffect(() => {
    if (!brouillonDeLHote) {
      // Brouillon retiré par l'hôte : ne pas garder ses pièces en référence.
      setBrouillonConfie(null);
      return;
    }
    setPiecesDuBrouillon(brouillonDeLHote.files ?? []);
    setBrouillonConfie(brouillonDeLHote);
  }, [brouillonDeLHote]);
  const onInitialFilesConsumed = useCallback(() => setPiecesDuBrouillon(null), []);

  // Deux temps à couvrir : le rendu où le brouillon vient d'arriver (l'effet
  // ci-dessus n'a pas encore tourné), puis ceux où ses pièces attendent que le
  // composeur les ait posées.
  const brouillonEnCoursDePose = piecesDuBrouillon !== null
    || (brouillonDeLHote !== null && brouillonDeLHote !== brouillonConfie);

  const enAttente = useFichiersRendusAuComposeurStore((state) => (
    conversationId ? state.parConversation[conversationId] ?? AUCUN_FICHIER : AUCUN_FICHIER
  ));
  const onReturnedFilesConsumed = useCallback((files: readonly File[]) => {
    if (conversationId) confirmerFichiersRendusAuComposeur(conversationId, files);
  }, [conversationId]);

  return {
    initialFiles: piecesDuBrouillon ?? undefined,
    onInitialFilesConsumed,
    returnedFiles: brouillonEnCoursDePose ? AUCUN_FICHIER : enAttente,
    onReturnedFilesConsumed,
  };
}
